// Canonical cross-market taxonomy — the single market-agnostic vocabulary the
// Screener filters and groups on. Each market's native classification (UK SIC,
// FR NAF, …) crosswalks INTO this; the native label is kept in the market
// enrichment tables for provenance/display.
//
// Sub-segment normalisation: markets emit a CANONICAL_SUBSEGMENT into
// entity_normalised_metrics.subsegment (+ the segment group). Pure
// manufacturing is intentionally NOT represented — those companies are out of
// scope for the platform.
//
// Service-quality normalisation: ratings are NOT absolute-comparable across
// regulators (CQC "Good" ≠ HAS "B"), so quality_tier is a RELATIVE band within
// each market's regulator scheme; quality_basis carries the native grade
// ("CQC · Good", "HAS · B") for display + audit.

export const CANONICAL_SUBSEGMENTS = [
  'Hospitals & clinics',
  'Primary care (GP)',
  'Specialist medical',
  'Dental',
  'Optical',
  'Audiology',
  'Physiotherapy & allied health',
  'Diagnostics & labs',
  'Pharmacy',
  'Pharma wholesale',
  'Medical equipment & supplies',
  'Dental laboratories & equipment',
  'Veterinary',
  'Residential elderly care',
  'Residential specialist care',
  'Domiciliary / home care',
  'Ambulance / patient transport',
  'Other healthcare',
] as const;
export type CanonicalSubsegment = (typeof CANONICAL_SUBSEGMENTS)[number];

export const CANONICAL_SEGMENT_GROUPS = [
  'Hospitals & acute',
  'Primary, specialist & allied care',
  'Pharmacy, diagnostics & supplies',
  'Residential & home care',
  'Veterinary',
] as const;

/** Canonical sub-segment → segment group. */
export const SUBSEGMENT_GROUP: Readonly<Record<CanonicalSubsegment, string>> = {
  'Hospitals & clinics': 'Hospitals & acute',
  'Primary care (GP)': 'Primary, specialist & allied care',
  'Specialist medical': 'Primary, specialist & allied care',
  Dental: 'Primary, specialist & allied care',
  Optical: 'Primary, specialist & allied care',
  Audiology: 'Primary, specialist & allied care',
  'Physiotherapy & allied health': 'Primary, specialist & allied care',
  'Other healthcare': 'Primary, specialist & allied care',
  'Diagnostics & labs': 'Pharmacy, diagnostics & supplies',
  Pharmacy: 'Pharmacy, diagnostics & supplies',
  'Pharma wholesale': 'Pharmacy, diagnostics & supplies',
  'Medical equipment & supplies': 'Pharmacy, diagnostics & supplies',
  'Dental laboratories & equipment': 'Pharmacy, diagnostics & supplies',
  Veterinary: 'Veterinary',
  'Residential elderly care': 'Residential & home care',
  'Residential specialist care': 'Residential & home care',
  'Domiciliary / home care': 'Residential & home care',
  'Ambulance / patient transport': 'Residential & home care',
};

// ── Care cohort axis ────────────────────────────────────────────────────────
// Orthogonal to the segment group: splits the canonical sub-segments into
// PATIENT-FACING care (the clinics, homes, pharmacies a patient walks into) and
// NON-PATIENT-FACING supply chain (labs, wholesale, equipment/products makers —
// B2B businesses that serve providers, not patients). Different buyers, deal
// multiples and diligence, so a buy-and-build in *care* should not mix them in.
// This is the home for the non-clinical cohort (dental labs/equipment, pharma
// wholesale, medical supplies today; life sciences / med-tech / vet labs later).
export type SubsegmentCohort = 'patient_facing' | 'non_patient_facing';

/** Canonical sub-segment → care cohort. */
export const SUBSEGMENT_COHORT: Readonly<Record<CanonicalSubsegment, SubsegmentCohort>> = {
  'Hospitals & clinics': 'patient_facing',
  'Primary care (GP)': 'patient_facing',
  'Specialist medical': 'patient_facing',
  Dental: 'patient_facing',
  Optical: 'patient_facing',
  Audiology: 'patient_facing',
  'Physiotherapy & allied health': 'patient_facing',
  'Diagnostics & labs': 'patient_facing',
  Pharmacy: 'patient_facing',
  Veterinary: 'patient_facing',
  'Residential elderly care': 'patient_facing',
  'Residential specialist care': 'patient_facing',
  'Domiciliary / home care': 'patient_facing',
  'Ambulance / patient transport': 'patient_facing',
  'Other healthcare': 'patient_facing',
  // Non-patient-facing supply chain.
  'Pharma wholesale': 'non_patient_facing',
  'Medical equipment & supplies': 'non_patient_facing',
  'Dental laboratories & equipment': 'non_patient_facing',
};

/** The patient-facing care sub-segments — the default universe for care-side
 *  panels (roll-up, bolt-on, seller readiness, catchment). */
export const PATIENT_FACING_SUBSEGMENTS = CANONICAL_SUBSEGMENTS.filter(
  (s) => SUBSEGMENT_COHORT[s] === 'patient_facing',
);

/** True ⇔ the sub-segment is patient-facing care (not B2B supply chain). */
export function isPatientFacing(subsegment: string | null | undefined): boolean {
  return !!subsegment && SUBSEGMENT_COHORT[subsegment as CanonicalSubsegment] === 'patient_facing';
}

// ── Product scope (care-home HARD scope) ─────────────────────────────────────
// Product-owner decision 2026-09-29: the Terminal serves care homes only. An
// entity is reachable — listable, searchable, openable by id — only when its
// entity_normalised_metrics.subsegment (display label) is one of these. Anything
// else, including 'Domiciliary / home care', and any entity with no enm row, is
// out of scope. DELIBERATELY SEPARATE from `in_platform_scope` (a revenue floor
// with other consumers): this is a product boundary, not a data-quality gate.
// Server-side SQL renders from this list via @hc/db productScopeSql().
export const PRODUCT_SUBSEGMENTS = [
  'Residential elderly care',
  'Residential specialist care',
] as const satisfies readonly CanonicalSubsegment[];

export type ProductSubsegment = (typeof PRODUCT_SUBSEGMENTS)[number];

// Own-home care back in scope (product-owner decision 2026-10-02). NOT added to PRODUCT_SUBSEGMENTS: ~55,000
// entities carry 'Domiciliary / home care' and most are general home-care agencies, which stay OUT. An entity
// with this sub-segment is in scope ONLY when it operates at least one own-home visiting or scheme service in
// own_home_services (supported living, specialist / complex home care, intensive care at home, housing schemes).
// That needs a table lookup, so it is rendered server-side by @hc/db productScopeSql(); isInProductScope()
// below sees the label alone and keeps answering false for it.
export const OWN_HOME_SUBSEGMENT = 'Domiciliary / home care' as const satisfies CanonicalSubsegment;

/** True ⇔ the (enm display-label) sub-segment is inside the product scope. */
export function isInProductScope(subsegment: string | null | undefined): boolean {
  return !!subsegment && (PRODUCT_SUBSEGMENTS as readonly string[]).includes(subsegment);
}

/** UK native sub-segment label → canonical. */
export const UK_SUBSEGMENT_TO_CANONICAL: Readonly<Record<string, CanonicalSubsegment>> = {
  Hospitals: 'Hospitals & clinics',
  'GP practices': 'Primary care (GP)',
  'Specialist medical practice': 'Specialist medical',
  Dental: 'Dental',
  Opticians: 'Optical',
  'Audiology / hearing aids retail': 'Audiology',
  'Allied health / other': 'Physiotherapy & allied health',
  'Community pharmacy': 'Pharmacy',
  'Pharma wholesale': 'Pharma wholesale',
  'Medical / orthopaedic goods retail': 'Medical equipment & supplies',
  Veterinary: 'Veterinary',
  'Residential care (elderly)': 'Residential elderly care',
  'Nursing homes (medical)': 'Residential elderly care',
  'Residential nursing care': 'Residential elderly care',
  'Residential care (LD/MH)': 'Residential specialist care',
  'Residential care (other)': 'Residential specialist care',
  'Domiciliary / social care': 'Domiciliary / home care',
};

/** FR native sub-segment label → canonical. `null` = excluded (pure mfg). */
export const FR_SUBSEGMENT_TO_CANONICAL: Readonly<Record<string, CanonicalSubsegment | null>> = {
  'Hospitals & clinics': 'Hospitals & clinics',
  'GP / general practice': 'Primary care (GP)',
  'Specialist medical': 'Specialist medical',
  Dental: 'Dental',
  'Optical retail': 'Optical',
  'Physio / rehab / allied': 'Physiotherapy & allied health',
  'Nurses & midwives': 'Physiotherapy & allied health',
  'Other human health': 'Other healthcare',
  'Medical labs & diagnostics': 'Diagnostics & labs',
  Pharmacy: 'Pharmacy',
  'Pharma wholesale/mfg': 'Pharma wholesale',
  'Medical goods retail': 'Medical equipment & supplies',
  'Medical device mfg': null, // pure manufacturing — excluded
  Veterinary: 'Veterinary',
  'Residential medical care': 'Residential elderly care',
  'Residential social care': 'Residential specialist care',
  'Domiciliary / home care': 'Domiciliary / home care',
  'Ambulance / patient transport': 'Ambulance / patient transport',
  'Childcare & other social': 'Other healthcare',
};

/** DE native sub-segment label → canonical. Tallied from the staged
 *  Hetzner-era universe (46,097 rows, 18 native labels — the FR label family
 *  plus Audiology and GP). Unmapped → 'Other healthcare' (never dropped). */
export const DE_SUBSEGMENT_TO_CANONICAL: Readonly<Record<string, CanonicalSubsegment>> = {
  Pharmacy: 'Pharmacy',
  Dental: 'Dental',
  'Optical retail': 'Optical',
  'Hospitals & clinics': 'Hospitals & clinics',
  'Residential medical care': 'Residential elderly care',
  'Specialist medical': 'Specialist medical',
  'Physio / rehab / allied': 'Physiotherapy & allied health',
  'Domiciliary / home care': 'Domiciliary / home care',
  'Medical labs & diagnostics': 'Diagnostics & labs',
  'Pharma wholesale / mfg': 'Pharma wholesale',
  'Medical goods retail': 'Medical equipment & supplies',
  'Residential social care': 'Residential specialist care',
  'Ambulance / patient transport': 'Ambulance / patient transport',
  Audiology: 'Audiology',
  'Other human health': 'Other healthcare',
  'GP / general practice': 'Primary care (GP)',
  Veterinary: 'Veterinary',
  'Nurses & midwives': 'Physiotherapy & allied health',
};

/** DK native sub-segment label → canonical. Mirrors `dk_lib.CANONICAL`
 *  (the DK ingest pipeline writes `sub_segment_native`; the merge crosswalks
 *  to canonical for `entity_normalised_metrics.subsegment`). Labs / Audiology /
 *  Ambulance arrive already split out by the loader's name-rule overlay
 *  (`refine_subsegment`), so they appear as native labels here too. Anything
 *  unmapped → 'Other healthcare' (never dropped). */
export const DK_SUBSEGMENT_TO_CANONICAL: Readonly<Record<string, CanonicalSubsegment>> = {
  Hospitals: 'Hospitals & clinics',
  'GP practices': 'Primary care (GP)',
  'Specialist medical practice': 'Specialist medical',
  Dental: 'Dental',
  'Allied health / other': 'Physiotherapy & allied health',
  'Residential nursing care': 'Residential elderly care',
  'Residential care (LD/MH)': 'Residential specialist care',
  'Residential care (other)': 'Residential specialist care',
  'Domiciliary / social care': 'Domiciliary / home care',
  Veterinary: 'Veterinary',
  'Community pharmacy': 'Pharmacy',
  'Pharma wholesale': 'Pharma wholesale',
  Opticians: 'Optical',
  'Medical / orthopaedic goods retail': 'Medical equipment & supplies',
  'Diagnostics & labs': 'Diagnostics & labs',
  Audiology: 'Audiology',
  'Ambulance / patient transport': 'Ambulance / patient transport',
};

/** BE native sub-segment label → canonical. `null` = excluded (pure mfg /
 *  wholesale, out of the care-delivery + retail universe). Keyed on the BE
 *  ingest's native labels; the loader writes `sub_segment_native`, the merge
 *  crosswalks. Decisions: Mental health → Specialist medical (matches the UK
 *  enum mapping); Childcare → Other healthcare (in-scope per operator, not
 *  clinical); Residential split — the BE loader re-emits 872/873 as
 *  'Residential specialist care', leaving 'Residential care' = nursing-home
 *  871 → Residential elderly care. */
export const BE_SUBSEGMENT_TO_CANONICAL: Readonly<Record<string, CanonicalSubsegment | null>> = {
  'Hospitals & clinics': 'Hospitals & clinics',
  'GP / general practice': 'Primary care (GP)',
  'Specialist medical': 'Specialist medical',
  Dental: 'Dental',
  'Physio / rehab / allied': 'Physiotherapy & allied health',
  'Nurses & midwives': 'Physiotherapy & allied health',
  'Mental health / psychology': 'Specialist medical',
  'Medical imaging & labs': 'Diagnostics & labs',
  'Ambulance / patient transport': 'Ambulance / patient transport',
  'Alternative medicine': 'Other healthcare',
  'Other human health': 'Other healthcare',
  'Residential care': 'Residential elderly care',
  'Residential specialist care': 'Residential specialist care',
  'Home care & social (no accom.)': 'Domiciliary / home care',
  'Childcare & other social': 'Other healthcare',
  Pharmacy: 'Pharmacy',
  'Optical retail': 'Optical',
  'Medical & orthopaedic retail': 'Medical equipment & supplies',
  Veterinary: 'Veterinary',
  'Pharma wholesale': 'Pharma wholesale', // available if BE later includes healthcare wholesalers
  'Medical device mfg': null, // pure manufacturing — excluded
  'Pharma mfg': null, // pure manufacturing — excluded
};

// ── GB diagnostics refinement ───────────────────────────────────────────────
// UK SIC cannot distinguish a pathology/imaging/diagnostics provider from a
// generic clinic: labs, imaging and pathology mostly file under the broad
// catch-all SIC 86900 ("Other human health"), and consultant-led imaging /
// pathology practices file under 86220 ("Specialist medical"). Both get routed
// into "Specialist medical" / "Physiotherapy & allied health" / "Other
// healthcare" by the enum→canonical map, so GB shows ZERO "Diagnostics & labs"
// while FR (which has a dedicated NAF 8690B → canonical mapping) shows ~542.
//
// This refinement splits diagnostics out of the SIC bucket using NAME keywords.
// It is PRECISION-FIRST: it only re-tags an entity that is already in a human
// health SIC (86xxx, excluding 86230 dental), whose name carries a diagnostics
// term, and whose name does NOT carry a cross-sector token (dental-prosthetics
// labs, veterinary, optical, print/photo imaging) that would make the keyword a
// false positive. Mirrors FR's 'Medical labs & diagnostics' → 'Diagnostics &
// labs' so the canonical bucket is populated consistently across markets.

/** Diagnostics / pathology / imaging name tokens (tested case-insensitively). */
const GB_DIAGNOSTICS_NAME_RE =
  /pathology|patholog|laborator|diagnostic|radiolog|histolog|cytolog|phlebotom|haematolog|hematolog|\bimaging\b|\bmri\b|\bct scan|ultrasound|\bscan(s|ning)?\b|\bx-?ray|sonograph|microbiolog|nuclear medicine|\bblood test/i;

/**
 * Cross-sector tokens that make a diagnostics keyword a false positive: dental
 * laboratories make dentures/crowns (not diagnostics); veterinary/optical are
 * their own canonical buckets; print/photo "imaging" is non-medical.
 */
const GB_DIAGNOSTICS_EXCLUDE_RE =
  /dental|orthodont|denture|prosthet|\bvet\b|veterinar|\bequine|\bcanine|optical|spectacle|hearing|photograph|\bprint/i;

/**
 * True when a GB entity should be re-tagged to the 'Diagnostics & labs'
 * canonical sub-segment based on its legal name and UK SIC. Requires a human
 * health SIC (86xxx, not 86230 dental) so the keyword can't fire on unrelated
 * companies. Used by both the GB recompute (forward) and the backfill (existing
 * data) so the rule lives in one place.
 */
export function isGbDiagnosticsByName(
  legalName: string | null | undefined,
  sicCode: string | null | undefined,
): boolean {
  if (!legalName || !sicCode) return false;
  if (!sicCode.startsWith('86')) return false; // human-health SIC only
  if (sicCode === '86230') return false; // dental SIC → dental-prosthetics labs
  if (!GB_DIAGNOSTICS_NAME_RE.test(legalName)) return false;
  if (GB_DIAGNOSTICS_EXCLUDE_RE.test(legalName)) return false;
  return true;
}

// ── GB optical-in-audiology refinement ──────────────────────────────────────
// UK SIC 47741 is the *hearing-aids retail* code → it maps to Audiology, which
// is correct for the ~250 genuinely-hearing operators that file under it. But
// opticians routinely self-file under 47741 instead of 47782 (opticians), so a
// handful of clear OPTICAL retailers land in Audiology and distort that thin
// pool (e.g. a £20m optician outsizing a 4-operator audiology build). This
// re-tags them to Optical. Precision-first, mirroring the diagnostics rule: it
// fires ONLY on the hearing-aids SIC, for a clearly-optical name that carries NO
// hearing/audio token — so genuine combined optical+hearing chains stay put.

/** Optical-retailer name tokens (tested case-insensitively). */
const GB_OPTICAL_NAME_RE = /optical|optician|optometr|spectacle|eyewear|eye\s?care/i;
/** Hearing tokens that veto the re-tag (a combined optical+hearing operator). */
const GB_HEARING_NAME_RE = /hearing|audio|audiolog/i;

/**
 * True when a GB entity sitting in Audiology (UK SIC 47741, the hearing-aids
 * retail code) is actually an optician misfiled under that code and should be
 * re-tagged to the 'Optical' canonical sub-segment. Used by both the GB
 * recompute (forward) and the backfill (existing data) so the rule lives once.
 */
export function isGbOpticalMisfiledByName(
  legalName: string | null | undefined,
  sicCode: string | null | undefined,
): boolean {
  if (!legalName || sicCode !== '47741') return false; // hearing-aids retail SIC only
  if (!GB_OPTICAL_NAME_RE.test(legalName)) return false;
  if (GB_HEARING_NAME_RE.test(legalName)) return false; // combined optical+hearing → leave
  return true;
}

// ── FR veterinary-diagnostics refinement ────────────────────────────────────
// FR NAF 7500Z covers every "activité vétérinaire", so veterinary DIAGNOSTICS
// LABS (IDEXX's French arm, LAPVSO Toulouse, Vetodiag) land in 'Veterinary'
// alongside the practices. They sell analyses to vets, not care to owners —
// the canonical bucket is 'Diagnostics & labs' (as FR human labs already
// are). Precision-first mirror of isGbDiagnosticsByName: fires only on the
// vet NAF, for an explicit lab/diagnostics name, with clinical tokens
// vetoing (a "clinique vétérinaire" that mentions its in-house lab stays a
// practice).

/** Vet-lab name tokens: generic lab/pathology terms + known diagnostics brands. */
const FR_VET_DIAGNOSTICS_NAME_RE =
  /laborato|patholog|diagnostic|vetodiag|idexx|zoetis|\bscil\b|analyses?\s+v[eé]t[eé]rinaire/i;
/** Clinical tokens that veto the re-tag — the entity treats animals itself. */
const FR_VET_DIAGNOSTICS_EXCLUDE_RE = /clinique|cabinet|\bchv\b|h[oô]pital|urgence/i;

/**
 * True when an FR entity filed under the veterinary NAF should be re-tagged to
 * 'Diagnostics & labs' based on its legal name. Used by the FR recompute
 * (forward) and the one-off backfill so the rule lives in one place.
 */
export function isFrVetDiagnosticsByName(
  legalName: string | null | undefined,
  nafCode: string | null | undefined,
): boolean {
  if (!legalName || !nafCode) return false;
  if (nafCode.replace('.', '').toUpperCase() !== '7500Z') return false;
  if (!FR_VET_DIAGNOSTICS_NAME_RE.test(legalName)) return false;
  if (FR_VET_DIAGNOSTICS_EXCLUDE_RE.test(legalName)) return false;
  return true;
}

// ── Dental supply-chain refinement ──────────────────────────────────────────
// UK SIC 86230 ("dental practice activities") is used by BOTH patient-facing
// dental practices AND their non-patient-facing supply chain — dental
// laboratories (crowns/dentures/prosthetics), and dental equipment/products
// makers/distributors that self-file under the dental code. The enum→canonical
// map routes all of them into the 'Dental' care bucket, where they inflate the
// dental *care* market size and CR5/HHI and pollute a care buy-and-build's
// target universe (they're B2B, not clinics). This splits the suppliers out into
// the non-patient-facing 'Dental laboratories & equipment' sub-segment by name.
//
// Precision-first, mirroring the diagnostics/optical rules: fires only for a
// STRONG supplier token, and never when the name carries a care-provider token
// (practice / surgery / clinic / dentist / studio) that marks a real clinic.

/** Dental supply-chain name tokens (lab / prosthetics / equipment / products). */
const DENTAL_SUPPLIER_NAME_RE =
  /laborator|dentures?|prosthet|manufactur|dental[^a-z]{0,3}(products?|equipment|supplies|supply|technolog|ceramics?)/i;

/** Care-provider tokens that veto the re-tag (the entity is a real clinic). */
const DENTAL_CARE_NAME_RE = /\bpractice|\bsurger|\bclinic|dentist|\bstudio|orthodontic practice/i;

/**
 * True when a GB entity currently in the 'Dental' care bucket is actually a
 * dental laboratory or equipment/products supplier and should be re-tagged to
 * the non-patient-facing 'Dental laboratories & equipment' sub-segment. Name-
 * based (dental labs share SIC 86230 with practices, so the code can't split
 * them). The caller gates on GB + subsegment='Dental'; a zero-CQC-site guard is
 * added by the backfill for extra safety on existing rows. Used by both the GB
 * recompute (forward) and the backfill (existing data) so the rule lives once.
 */
export function isDentalSupplierByName(legalName: string | null | undefined): boolean {
  if (!legalName) return false;
  if (!DENTAL_SUPPLIER_NAME_RE.test(legalName)) return false;
  if (DENTAL_CARE_NAME_RE.test(legalName)) return false;
  return true;
}

// ── GB Other-healthcare name-evident re-tag ─────────────────────────────────
// UK SIC 86900 ("other human health activities") is a self-declared catch-all:
// most of its residents genuinely defy finer classification, but a handful
// state their true sub-segment in their legal name (a pharmacy, a nursing
// home, a domiciliary agency, an optician that never picked the right code).
// Re-tag ONLY those. Precision-first, mirroring the diagnostics/optical/dental
// rules: each token names the business type unambiguously; "pharma" (services,
// wholesale, consulting) deliberately does NOT match — only "pharmacy" does.

/** (token → canonical) pairs, tested in order; first match wins. */
const GB_OTHER_HEALTHCARE_RETAG: ReadonlyArray<[RegExp, CanonicalSubsegment]> = [
  [/\bpharmacy\b/i, 'Pharmacy'],
  [/nursing home/i, 'Residential elderly care'],
  [/domiciliary|home care/i, 'Domiciliary / home care'],
  [/\boptical\b|\boptician/i, 'Optical'],
];
/** Hearing tokens veto the Optical re-tag (combined optical+hearing stays). */
const GB_OTHER_HEALTHCARE_HEARING_RE = /hearing|audio|audiolog/i;

/**
 * Canonical sub-segment a GB SIC-86900 entity should be re-tagged to based on
 * its legal name, or null to leave it in 'Other healthcare'. Fires only for
 * SIC 86900 so the tokens can't reach entities already classified elsewhere.
 * Used by both the GB recompute (forward) and any backfill (existing data) so
 * the rule lives in one place.
 */
export function gbOtherHealthcareRetagByName(
  legalName: string | null | undefined,
  sicCode: string | null | undefined,
): CanonicalSubsegment | null {
  if (!legalName || sicCode !== '86900') return null;
  for (const [re, target] of GB_OTHER_HEALTHCARE_RETAG) {
    if (re.test(legalName)) {
      if (target === 'Optical' && GB_OTHER_HEALTHCARE_HEARING_RE.test(legalName)) return null;
      return target;
    }
  }
  return null;
}

// ── GB entity-specific sub-segment overrides ────────────────────────────────
// A few large entities self-declare a healthcare SIC that lands them in a
// patient-facing care bucket their business does not belong to, and no name
// token can express the rule (the name is just "<Brand> Healthcare"). Keyed by
// Companies House number — the stable registry identity — and applied LAST in
// the GB classification chain so an override always wins. Keep this list
// short and documented; it is a register of adjudicated facts, not a dumping
// ground.
export const GB_SUBSEGMENT_OVERRIDES_BY_COMPANY: Readonly<Record<string, CanonicalSubsegment>> = {
  // Otto Bock Healthcare plc — prosthetics/orthotics manufacturer (SIC 86900
  // self-declared); B2B supply chain, not patient-facing care.
  '01271967': 'Medical equipment & supplies',

  // ── NHS General Dental Services contract holders, adjudicated 2026-09-08 ──
  // Each entity below holds one or more NHS England dental contracts linked by
  // practice postcode + CQC location + name agreement (two keys, see
  // handover/GB_DENTAL_NHS_CONTRACT_LINK_2026-09-08.md) AND every one of its
  // active CQC locations is a 'Dentist' service — yet a self-declared SIC of
  // 86210 (GP), 86220 (specialist) or 86900 (other) had routed it elsewhere.
  // A GDS contract is decisive evidence of dentistry. Two contract holders were
  // inspected and deliberately NOT overridden: City Health Care Partnership CIC
  // (06273905, community services with a community-dental line) and Cumbria
  // Health Limited (03121117, GP/out-of-hours with one dental contract) — both
  // are genuine mixed providers whose CQC locations are not dental.
  '06740579': 'Dental', // Portman Healthcare — 180 CQC dentist locations, 34 GDS contracts
  '09315294': 'Dental', // Envisage Dental UK — 87 dentist locations, 16 contracts
  '06502955': 'Dental', // Seaside Dental Practice — SIC 86210, 1 dentist location
  '04800776': 'Dental', // Facial Aesthetics Ltd — dentist-led practice, GDS contract
  '06737373': 'Dental', // R G Mattey Limited — 1 dentist location
  '07188478': 'Dental', // Dr Sanjay Gupta Limited — SIC 86210, 1 dentist location
  '11747270': 'Dental', // Ujjan Limited — SIC 86210, 1 dentist location
  '11835124': 'Dental', // PSDP Ltd — SIC 86210, 1 dentist location
  '08638204': 'Dental', // M K Vasant & Associates — SIC 86210, 1 dentist location
  '09948775': 'Dental', // Kevom Ltd — 6 locations, all dentist-typed, 2 contracts
  '15902193': 'Dental', // Adel and Agila Oral Surgery — SIC 86210, 1 dentist location
  '11662292': 'Dental', // Clarity Dental Clinic — SIC 86900, 1 dentist location
  '16791738': 'Dental', // Sedbergh Dental Practice — 1 dentist location
  '09609006': 'Dental', // NEL Healthcare Limited — SIC 86220, its only location is a dentist
  '07676988': 'Dental', // David Cheung Ltd — SIC 86220, 1 dentist location, 2 contracts
  '11854235': 'Dental', // Patel & Makwana Dentique — SIC 86210, 1 dentist location
  '15038935': 'Dental', // Saving Smiles Northampton — SIC 86210, 1 dentist location
};

// ── NHS community pharmacy contract holders (GB) ─────────────────────────────
// An entity on the NHSBSA consolidated pharmaceutical list with a Community or
// LPS contract IS the contractor operating a registered pharmacy premises, so
// that contract is decisive evidence of the Pharmacy subsegment — the same
// standing a General Dental Services contract has for Dental. This is applied
// as a RULE rather than a list of company numbers: 236 GB entities were
// mis-routed by a self-declared SIC (86210 GP, 46460 wholesale, 47749 medical
// goods) and enumerating them would put a data extract in a source file that
// exists for adjudicated exceptions. The rule reads its evidence from
// entity_sites (source `nhsbsa_pharmacy_list`), so it also self-corrects when
// the register drops a contractor. See handover/GB_PHARMACY_SITE_SPINE_2026-09-08.md.
//
// Dispensing appliance contractors are NOT caught by it: they land under
// site_specialty 'Medical equipment & supplies', and the rule reads only
// 'Pharmacy' premises.
//
// The exclusions below are the mixed providers, adjudicated one at a time on
// 2026-09-08 by the same test the dental pass used — a company that also holds
// CQC registrations is running a regulated-care business alongside the
// pharmacy, and calling it a pharmacy would be a worse description than the one
// it has. Six of the 242 candidates hold any CQC registration at all, and all
// six are excluded; the remaining 236 hold none.
export const GB_PHARMACY_RETAG_EXCLUSIONS: ReadonlySet<string> = new Set([
  '06982573', // Pharmaxo Pharmacy Services — clinical homecare + nurse agency, 3 CQC locations
  '02764914', // Lloyds Clinical — clinical homecare, 8 CQC locations (community nursing, homecare)
  '06609214', // Christchurch Pharmacy — carries a CQC Doctors/Gps registration; conflicting evidence
  '06938232', // New River Health — GP practice with one pharmacy
  '04783907', // Hughenden Valley Pharma — carries a CQC Doctors/Gps registration
  '08589394', // Smarta Healthcare — homecare agency with one pharmacy
]);

/**
 * Pharmacy, when the entity holds an NHS community pharmacy contract and is not
 * an adjudicated mixed provider. Returns undefined when the rule does not fire,
 * so a caller can fall through to its own classification.
 */
export function gbPharmacyContractRetag(
  hasNhsPharmacy: boolean,
  registryId: string | null,
  currentSubsegment: string | null,
): CanonicalSubsegment | undefined {
  if (!hasNhsPharmacy) return undefined;
  if (currentSubsegment === 'Pharmacy') return undefined;
  if (registryId && GB_PHARMACY_RETAG_EXCLUSIONS.has(registryId)) return undefined;
  return 'Pharmacy';
}

// ── GB care specialty split (revenue sub-subsegment axis, platform-owned) ─────
// The revenue specialty tier breaks the 3 expanded canonical parents into finer
// buckets (SPECIALTIES above). Per the per-market capability matrix
// (handover/subsegments/specialty_market_capability_25markets.csv), GB
// `nursing_home` + `residential_care` are owner=platform, grade=native — the
// platform derives them from SIC + the CQC care-home service type. (dialysis is
// also platform-owned but not detectable in the current CSV-sourced service_types;
// the name-based specialties — oncology/imaging/pathology/… — are owner=model and
// are left to the calibration side's Drop-1 emission.)
//
// This populates ONLY the classification/display fields (primary_specialty +
// grade + basis + specialties_present) — NEVER model_subsegment (the revenue
// routing, which stays the calibration agents' Drop-2). `basis='platform_interim'`
// marks it so an agent's authoritative entity_specialty_GB.csv cleanly overrides
// it. Mirrors the isGb*ByName refinements: platform-side GB, one place, reused by
// the backfill.

export interface CareSpecialtyResult {
  /** Finest bucket, or null when the operator sits at its parent. */
  primary: 'nursing_home' | 'residential_care' | null;
  /** Detection grade (native = SIC/CQC-service-type derived). */
  grade: 'native' | null;
  /** Fine provenance (display/filter only, never routes). */
  basis: string | null;
  /** Every care specialty present (an operator can run both with- and
   *  without-nursing sites). */
  present: Array<'nursing_home' | 'residential_care'>;
}

const CARE_WITH_NURSING_RE = /nursing home|with nursing|nursing \(nh\)/i;
const CARE_WITHOUT_NURSING_RE = /care home service|without nursing/i;

/**
 * Split a GB 'Residential elderly care' operator into nursing_home (with nursing)
 * vs residential_care (without nursing) from its rolled-up CQC service types + UK
 * SIC. `primary` prefers nursing_home when both are present (the higher-acuity,
 * higher-revenue setting). Returns all-null for non-care rows so the caller can
 * skip. Used by the GB care-specialty backfill (and any forward recompute).
 */
export function classifyGbCareSpecialty(
  subsegment: string | null | undefined,
  serviceTypes: readonly string[] | null | undefined,
  nace: string | null | undefined,
): CareSpecialtyResult {
  const empty: CareSpecialtyResult = { primary: null, grade: null, basis: null, present: [] };
  if (subsegment !== 'Residential elderly care') return empty;
  const svc = (serviceTypes ?? []).join(' | ');
  const sic = (nace ?? '').replace(/\D/g, '');
  const withNursing = CARE_WITH_NURSING_RE.test(svc) || sic === '87100' || sic === '86102';
  const withoutNursing = CARE_WITHOUT_NURSING_RE.test(svc) || sic === '87300';
  const present: Array<'nursing_home' | 'residential_care'> = [];
  if (withNursing) present.push('nursing_home');
  if (withoutNursing) present.push('residential_care');
  if (present.length === 0) return empty;
  return {
    primary: withNursing ? 'nursing_home' : 'residential_care',
    grade: 'native',
    basis: 'platform_interim',
    present,
  };
}

/** Platform-wide relative quality tiers (best → worst), plus the unrated state. */
export const QUALITY_TIERS = ['Strong', 'Good', 'Weak'] as const;
export const QUALITY_NOT_RATED = 'Not rated';

/** FR HAS evaluation grade (A best … D worst) → relative tier. */
export const HAS_GRADE_TO_TIER: Readonly<Record<string, (typeof QUALITY_TIERS)[number]>> = {
  A: 'Strong',
  B: 'Good',
  C: 'Weak',
  D: 'Weak',
};

// ── Specialty tier ──────────────────────────────────────────────────────────
// The canonical-17 stays the stable backbone; three of its parents expand into a
// finer specialty tier (10), one assigned per operator as model_subsegment =
// finest the market can detect+anchor, else the parent residual / canonical. The
// canonical TOKEN map below is NEW — enm.subsegment stores DISPLAY LABELS, no
// tokens existed. Frozen tokens; agreed with the market-data side.
// See handover/subsegments/Subsegment_Specialty_Platform_Build_Spec.md.

/** Canonical display label → stable machine token. */
export const CANONICAL_SUBSEGMENT_TOKEN: Readonly<Record<CanonicalSubsegment, string>> = {
  'Hospitals & clinics': 'hospitals_clinics',
  'Primary care (GP)': 'primary_care_gp',
  'Specialist medical': 'specialist_medical',
  Dental: 'dental',
  Optical: 'optical',
  Audiology: 'audiology',
  'Physiotherapy & allied health': 'physio_allied',
  'Diagnostics & labs': 'diagnostics_labs',
  Pharmacy: 'pharmacy',
  'Pharma wholesale': 'pharma_wholesale',
  'Medical equipment & supplies': 'medical_equipment',
  'Dental laboratories & equipment': 'dental_lab_equipment',
  Veterinary: 'veterinary',
  'Residential elderly care': 'residential_elderly',
  'Residential specialist care': 'residential_specialist',
  'Domiciliary / home care': 'domiciliary_home',
  'Ambulance / patient transport': 'ambulance_transport',
  'Other healthcare': 'other_healthcare',
};

/** The 10 specialties beneath the 3 expanded canonical parents. */
export const SPECIALTIES = [
  { token: 'dialysis', label: 'Dialysis / renal care', parent: 'Specialist medical' },
  { token: 'oncology', label: 'Oncology / cancer care', parent: 'Specialist medical' },
  { token: 'radiotherapy', label: 'Radiotherapy', parent: 'Specialist medical' },
  { token: 'fertility_ivf', label: 'Fertility / IVF', parent: 'Specialist medical' },
  { token: 'ophthalmology', label: 'Ophthalmology', parent: 'Specialist medical' },
  { token: 'imaging', label: 'Imaging', parent: 'Diagnostics & labs' },
  { token: 'pathology', label: 'Pathology / labs', parent: 'Diagnostics & labs' },
  {
    token: 'nursing_home',
    label: 'Nursing home (with nursing)',
    parent: 'Residential elderly care',
  },
  {
    token: 'residential_care',
    label: 'Residential care (no nursing)',
    parent: 'Residential elderly care',
  },
  { token: 'assisted_living', label: 'Assisted living', parent: 'Residential elderly care' },
] as const satisfies ReadonlyArray<{ token: string; label: string; parent: CanonicalSubsegment }>;

export type SpecialtyToken = (typeof SPECIALTIES)[number]['token'];

/** Specialty token → canonical parent display label. */
export const SPECIALTY_PARENT = Object.fromEntries(
  SPECIALTIES.map((s) => [s.token, s.parent]),
) as Readonly<Record<SpecialtyToken, CanonicalSubsegment>>;

/** Specialty token → display label (for the Screener's detailed-subsegment
 *  filter, which stores the token but shows the label). */
export const SPECIALTY_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  SPECIALTIES.map((s) => [s.token, s.label]),
);

/** Set of the fine specialty tokens — lets a mixed (canonical-label ∪ specialty-
 *  token) selection be split back into the two axes at query-build time. */
export const SPECIALTY_TOKENS: ReadonlySet<string> = new Set(SPECIALTIES.map((s) => s.token));

/** The Sub-segment filter's option order: each canonical-17 parent immediately
 *  followed by its fine specialty children (the 3 expanded parents only). A
 *  hierarchical single dropdown — NOT a separate "detailed" filter. */
// PATIENT-FACING ONLY (Alex 2026-08-02). The platform is an M&A tool for
// patient-facing care, so the B2B supply chain (Pharma wholesale, Medical
// equipment & supplies, Dental laboratories & equipment) is not offered as a
// filter choice anywhere in the UI. Their entities are also held out of
// in_platform_scope by the recompute, so offering them would only ever return
// an empty list. Stored data is untouched — flip SUBSEGMENT_COHORT and recompute
// to bring one back.
// CARE-HOME ONLY (Alex 2026-10-04). The Terminal is the care-home universe: non-care entities stay in
// the database but are not offered, not shown and not called. The filter list is therefore built from
// PRODUCT_SUBSEGMENTS plus the own-home label, not from every patient-facing sub-segment. Measured the
// day this landed: /facilities had no scope condition at all, so an unfiltered map returned 84% non-care
// points (pharmacies, dentists, GPs, hospitals) out of 985,147 — the gate in routes/facilities.ts is the
// other half of this change. Stored data is untouched; widen this list and the gate together to bring a
// sub-segment back.
export const SUBSEGMENT_FILTER_OPTIONS: readonly string[] = [
  ...PRODUCT_SUBSEGMENTS,
  OWN_HOME_SUBSEGMENT,
].flatMap((c) => [c as string, ...SPECIALTIES.filter((s) => s.parent === c).map((s) => s.token)]);

/**
 * TRUE ⇔ this (specialty, subsegment) pair is legal under the taxonomy.
 *
 * Every specialty token has exactly ONE contracted parent — SPECIALTY_PARENT is
 * built with Object.fromEntries, so a token cannot carry two without silently
 * losing one. A pair that violates the contract is not a harmless curiosity:
 * the Screener and Facilities filters match `primary_specialty IN (...)` with
 * NO parent guard, and the UI offers each token under its contracted parent —
 * so an out-of-contract stamp makes one parent's filter return another parent's
 * entities. Measured 2026-08-11: ~10,400 such rows, led by 7,168 Residential
 * SPECIALIST care operators stamped `residential_care`, whose only contracted
 * parent is Residential ELDERLY care.
 *
 * Legal forms: the contracted (token → parent) pair; a residual bucket under
 * its own parent; or a token identical to the parent's own slug (the
 * "sub-segment with no children" case, e.g. Dental/dental).
 */
export function isSpecialtyUnderParent(
  specialty: string | null | undefined,
  subsegment: string | null | undefined,
): boolean {
  if (!specialty || !subsegment) return false;
  if (SPECIALTY_PARENT[specialty as SpecialtyToken] === subsegment) return true;
  if (RESIDUAL_PARENT[specialty] === subsegment) return true;
  return specialty === subsegment.toLowerCase().replace(/ & /g, '_').replace(/ /g, '_');
}

/** Residual bucket → the parent it is the residue OF. */
const RESIDUAL_PARENT: Readonly<Record<string, string>> = {
  specialist_medical_other: 'Specialist medical',
  diagnostics_other: 'Diagnostics & labs',
  residential_elderly_other: 'Residential elderly care',
};

/** Residual model buckets — the parent AFTER its specialties are pulled out (a
 *  distinct coefficient from the legacy all-in parent). */
export const RESIDUAL_MODEL_BUCKETS = [
  'specialist_medical_other',
  'diagnostics_other',
  'residential_elderly_other',
] as const;
export type ResidualModelBucket = (typeof RESIDUAL_MODEL_BUCKETS)[number];

/** Per-row detectability grade (how THIS operator resolved). */
export type SpecialtyGrade = 'native' | 'register' | 'name' | 'unresolved';
/** Per-(market × specialty) detection ceiling (the capability table). */
export type CeilingGrade = 'native' | 'register' | 'register_friction' | 'name' | 'none';
/** Detectability-projected launch state. */
export type SpecialtyLaunchState = 'full' | 'partial' | 'hidden';

/** Detection-grade ordering for the routing gate. Higher = more authoritative.
 *  `unresolved` is the floor; a bucket's `grade_threshold` is the MINIMUM grade
 *  whose operators route to the niche formula. */
export const SPECIALTY_GRADE_RANK: Readonly<Record<SpecialtyGrade, number>> = {
  native: 3,
  register: 2,
  name: 1,
  unresolved: 0,
};

/** TRUE ⇔ `grade` is authoritative enough to clear the bucket's threshold. */
export function meetsGradeThreshold(grade: SpecialtyGrade, threshold: SpecialtyGrade): boolean {
  return SPECIALTY_GRADE_RANK[grade] >= SPECIALTY_GRADE_RANK[threshold];
}

/**
 * Per-bucket revenue-routing config carried in `model_export_<CC>.json`. The
 * calibrator OWNS every field here; the platform APPLIES them at recompute. This
 * is the canonical field-name contract the market briefs reference.
 *
 * Routing rule (platform-side, per the serve contract — D1):
 *   model_subsegment = (own_formula && meetsGradeThreshold(grade, grade_threshold))
 *                        ? bucket                      // niche formula
 *                        : <canonical parent residual> // parent / pooled
 *
 * `basis` (the fine provenance string) is display/filter only and never enters
 * this routing — only the coarse `grade` does.
 */
export interface SpecialtyBucketConfig {
  /** The specialty token (or residual/canonical token) this config routes. */
  bucket: SpecialtyToken | ResidualModelBucket | CanonicalSubsegment;
  /** Economically distinct → earns its own formula. Most niche buckets are
   *  LABELS (false) and inherit the parent/pooled formula. */
  own_formula: boolean;
  /** Minimum detection grade an operator needs to route to the niche formula. */
  grade_threshold: SpecialtyGrade;
  /** Audit note: why own_formula is true/false (beats-parent-on-reported verdict). */
  distinctness_verdict: string;
  /** Slope coefficients (the intercept lives in `market_level_anchor`). Shared /
   *  trained-elsewhere for pooled buckets — D2 of the calibration findings. */
  pooled_slope: number[];
  /** Re-leveling intercept for a waived market (FR: this is coef[0]). NULL ⇒ use
   *  the bucket's own fitted intercept. Calibrator-derived from local comps /
   *  reimbursement index / capacity; the platform only substitutes it. */
  market_level_anchor: number | null;
  /** Shrunk (empirical-Bayes) confidence P for this cell, 0..1. */
  confidence_P: number;
}

// ── Non-provider contamination (all markets) ─────────────────────────────────
// Companies that are not care providers at all, but sit inside patient-facing
// sub-segments because the source classification keys on an industry code the
// entity happens to carry. Measured 2026-08-03: 82 entities, ~EUR 0.96bn — small
// by count, but concentrated at the top of every revenue ranking, which is
// exactly where they distort CR5/HHI, the buy-list and any "biggest ungrouped"
// worklist. Two classes:
//
//   ANCILLARY SERVICES to hospitals — catering, cleaning, laundry, facility
//   management. Real businesses, often subsidiaries of a hospital group
//   (HELIOS Catering Hessen, Universitätsklinikum Köln Catering, KGM Krankenhaus
//   Gebäudemanagement). Healthcare-adjacent but not patient-facing care, so they
//   fall under the same judgement as the 2026-08-02 patient-facing re-scope.
//
//   OUTRIGHT NON-HEALTHCARE — Unify Communications (telecoms, EUR 188m, filed
//   under Diagnostics & labs), BSH Drives and Pumps (EUR 176m, Primary care),
//   Brose Prievidza (automotive, EUR 211m), FORTISCHEM (chemicals, EUR 147m),
//   and whole universities (Univerzita Komenského, EUR 219m, Physiotherapy).
//
// Deliberately NOT flagged, because each is a legitimate provider whose name
// merely contains a trigger word: medical/sanitary TRANSPORT (a real Ambulance
// sub-segment), analytical/pharmaceutical CHEMIE laboratories (real Diagnostics),
// and teaching hospitals ("University Hospital", "Universitätsklinikum") — which
// is why the university rule requires the ABSENCE of a hospital word.
// Individually verified non-healthcare companies whose NAMES carry no industry
// signal, so no regex can reach them. Kept deliberately short and exact-match:
// this is a manual override, not a heuristic, and every entry was confirmed by
// looking the company up. Brose Prievidza is an automotive-parts plant and
// FORTISCHEM a chemicals producer; both were flagged in the 2026-08-02 SK
// contamination note carrying EUR 211m and EUR 147m inside care sub-segments.
// Achema and SIRONA were found the same way in August 2026, by a sweep for
// entities whose revenue is absurd for their sub-segment
// (packages/db/scripts/audit-scope-outliers.ts, built after Achema surfaced
// during a Lithuanian spot-check). Achema is the largest chemical and fertilizer
// producer in the Baltics, sitting in **Primary care (GP)** at EUR 590m — 15% of
// Lithuania's entire in-scope revenue — because it carries no NACE code at all,
// so a name-based fallback classified it with nothing to contradict it. SIRONA
// manufactures dental equipment (Dentsply Sirona); it is not a dental practice.
const VERIFIED_NON_PROVIDERS = new Set<string>([
  'brose prievidza, spol. s r.o.',
  'fortischem a. s.',
  'akcinė bendrovė "achema"',
  'sirona dental systems gmbh',
  // Airport staffing/ground-operations contractor carrying a UK health SIC,
  // modelled at EUR 101m inside Other healthcare (2026-08-11 audit of the
  // >=12m turnover cohort). Adjudicated by Alex: staffing, not care.
  'team spirits airport operations limited',
  // Employer mental-health BENEFITS platform (EUR 30.5m reported, SIC 86900).
  // Sells a wellbeing product to employers; it is not a care provider and no
  // patient walks into it. Considered and REJECTED: minting a B2B
  // health-services canonical sub-segment for it — that is a platform-wide
  // taxonomy change for one or two material entities, and the neighbouring
  // cohort it would sweep in (health screening, occupational health) genuinely
  // IS patient-facing. The adjudicated register is the proportionate tool.
  'lyra health international limited',
  // Automotive plants sitting in the CZ universe as Primary care (GP) — found
  // 2026-08-12 when the sbírka-listin load surfaced their (real) turnover under
  // a health subsegment. Pre-existing universe contamination, same class as the
  // SK sibling 'brose prievidza' above.
  'brose cz spol. s r.o.',
  'zf automotive czech s.r.o.',
]);

const ANCILLARY_SERVICE_RE =
  /\b(catering|kantine|gro(ß|ss)k(ü|ue)che|reinigung|gebäudereinigung|cleaners|cleaning services|facility[- ]?management|gebäudemanagement|geb(ä|ae)udeservice|wäscherei|laundry|sicherheitsdienst|hausmeisterservice)\b/i;

const NON_HEALTHCARE_INDUSTRY_RE =
  /\b(automotive|automobil|kraftfahrzeug|maschinenbau|werkzeugbau|stahlbau|halbleiter|semiconductor|telekom|communications and collaboration|rechenzentrum|spedition|hochbau|tiefbau|bauunternehmen|immobilien|real estate|versicherungsmakler|assekuranz|leasing|druckerei|zeitarbeit|personaldienstleistung|windkraft|photovoltaik|supermarkt|einzelhandel|drives and pumps|hotelbetrieb|hotellerie)\b/i;

/**
 * "hospitality" is a SUBSTRING rule, deliberately unbounded on both sides.
 *
 * Word boundaries are wrong here in BOTH directions. Without any boundary, `hospital` matched
 * HOSPITALITY and 264 hotel companies sat in `Hospitals & clinics`. With \b on both sides, German
 * and English compounds evaded the fix — HospitalityGuys, Hospitalitygroup, HarzHospitality,
 * COM4HOSPITALITY, Cultuzz HospitalityWorks were all still admitted. The term never appears inside
 * a legitimate healthcare name, so it needs no boundary at all — which is NOT true of `hospital`,
 * `dental` or `reha`. Each false friend needs its own rule, not one global boundary policy.
 */
const HOSPITALITY_RE = /hospitality/i;

const UNIVERSITY_RE =
  /\b(univerzita|univerzity|uniwersytet|universit(ä|ae)t|universit(é|e)|universidad|universit(à|a)|university|vysoká škola|hochschule)\b/i;

// A teaching hospital is a provider; the university that owns it is not.
// NOTE: no trailing \b — these must match inflected and suffixed forms.
// "CLINICA UNIVERSIDAD DE NAVARRA" is a real Spanish private hospital and was a
// false positive while this used \bclinic\b, because the trailing boundary
// fails on "clinica". Same for Kliniken / Kliniky / hospitalet / ziekenhuizen.
//
// ⚠️ "hospital" MUST carry a negative lookahead for "ity". Dropping the trailing \b (above) to
// catch Kliniken/Clinica also made `hospital` match **HOSPITALITY** — and the hotel industry uses
// that word constantly. Measured 2026-09-09: 264 market participants were hotel and
// hotel-management companies sitting in `Hospitals & clinics` — Novum Hospitality, Sabre
// Hospitality Solutions, Foremost Hospitality, PIER Hospitality, German Hospitality Holding.
// A prefix match with no boundary is a real trade-off, not a free win; each such term needs its
// own false-friend guard. `hospitality` is also listed in NON_HEALTHCARE_INDUSTRY_RE so the name
// is positively rejected, not merely un-rescued.
const PROVIDER_CONTEXT_RE =
  /\b(klinik|hospital(?!ity)|nemocnic|szpital|ziekenhui|ospedale|hôpital|hospitalar|clinic|clínic|ambulanz|praxis|medical cent|zdravot|poliklinik|sanatorium|hospice|hospic)/i;

/**
 * True when an entity sitting in a patient-facing sub-segment is not a care
 * provider at all and should be held out of `in_platform_scope`.
 *
 * Name-based by necessity: the misclassification comes from the source industry
 * code, so the code itself cannot distinguish these. Ancillary and industrial
 * matches fire regardless of context — a hospital's catering subsidiary is still
 * catering. The university rule is the exception: it requires no hospital word,
 * so "Universitätsklinikum Köln" stays in scope while "Univerzita Komenského"
 * does not, and "Universitätsklinikum Köln Catering GmbH" is caught by the
 * ancillary rule instead.
 */
export function isNonProviderByName(legalName: string | null | undefined): boolean {
  if (!legalName) return false;
  if (VERIFIED_NON_PROVIDERS.has(legalName.trim().toLowerCase())) return true;
  if (ANCILLARY_SERVICE_RE.test(legalName)) return true;
  if (HOSPITALITY_RE.test(legalName)) return true;
  if (NON_HEALTHCARE_INDUSTRY_RE.test(legalName)) return true;
  if (UNIVERSITY_RE.test(legalName) && !PROVIDER_CONTEXT_RE.test(legalName)) return true;
  return false;
}
